/**
 * The submission package: one PDF an underwriter can actually read.
 *
 * Everything an agent assembles is scattered — account details in a database
 * row, questionnaire answers in another, ACORDs and loss runs as separate
 * files. An underwriter does not want six attachments and a covering note; they
 * want one document with a front page that says what this is.
 *
 * So this builds a branded cover and summary with jsPDF, then MERGES the
 * attachments into it with pdf-lib. That merge is the difference between a
 * cover sheet and a package.
 *
 * What cannot be merged is stated rather than dropped. A spreadsheet has no
 * pages to copy, so it appears in the contents marked "sent separately" — an
 * attachment silently missing from a package is how a submission gets declined
 * for something the agent actually supplied.
 */
import { jsPDF } from 'jspdf';
import { PDFDocument } from 'pdf-lib';
import type { FormAnswers, SubmissionData, SubmissionGrade, SupplementalForm } from '@commpass/shared';
import { LOB_NAME } from '@commpass/shared';

const NAVY = '#07385E';
const ORANGE = '#E3701E';
const MUTED = '#5F6B79';
const RULE = '#D8DCE1';

export interface PackageInput {
  id: string;
  className: string | null;
  data: SubmissionData;
  grade: SubmissionGrade;
  /** The class questionnaire and its answers, when one is attached. */
  supplemental?: { form: SupplementalForm; answers: FormAnswers } | null;
  /** Resolves a storagePath to a short-lived URL. Attachments are private. */
  resolveDoc: (storagePath: string) => Promise<string>;
  /**
   * Progress, so the caller can say what is happening.
   *
   * ⚠️ Merging a dozen PDFs runs on the main thread and blocks paint for tens
   * of seconds — the page looks crashed. Reporting between files gives React a
   * chance to render, which is as much the point as the message itself.
   */
  onProgress?: (step: string, done: number, total: number) => void;
  /**
   * Build only the summary page, leaving the documents to be attached in their
   * own right. Faster, keeps every file under its own name, and is the only way
   * Word and Excel reach the underwriter at all — they cannot be merged into a
   * PDF, so the merged packet could only ever list them as "sent separately".
   */
  coverOnly?: boolean;
}

interface Attachment {
  kind: string;
  name: string;
  storagePath: string;
  mime: string;
  merged: boolean;
  pages: number;
}

const money = (v: unknown): string => {
  const n = Number(String(v ?? '').replace(/[^0-9.]/g, ''));
  if (!v || !Number.isFinite(n) || n === 0) return String(v ?? '—');
  return n.toLocaleString('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 });
};

/**
 * Hand the browser a frame.
 *
 * Without this the whole build is one synchronous block: React sets "Building…"
 * but never gets to paint it, so the page freezes mid-click and looks crashed.
 */
const yieldToPaint = (): Promise<void> =>
  new Promise((r) => requestAnimationFrame(() => setTimeout(r, 0)));

export async function buildSubmissionPackage(input: PackageInput): Promise<Blob> {
  const { data, grade } = input;
  const doc = new jsPDF({ unit: 'pt', format: 'letter' });
  const W = doc.internal.pageSize.getWidth();
  const H = doc.internal.pageSize.getHeight();
  const M = 48;
  const BOTTOM = H - 56;
  let y = 0;

  const banner = (): void => {
    doc.setFillColor(NAVY);
    doc.rect(0, 0, W, 8, 'F');
    y = 54;
  };
  const room = (need: number): void => {
    if (y + need > BOTTOM) {
      doc.addPage();
      banner();
    }
  };
  const heading = (text: string): void => {
    room(46);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(12);
    doc.setTextColor(NAVY);
    doc.text(text, M, y);
    y += 7;
    doc.setDrawColor(RULE);
    doc.line(M, y, W - M, y);
    y += 16;
  };
  const row = (label: string, value: string): void => {
    if (!value || value === '—') return;
    const wrapped = doc.splitTextToSize(value, W - M * 2 - 168) as string[];
    room(wrapped.length * 13 + 4);
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(9.5);
    doc.setTextColor(MUTED);
    doc.text(label, M, y);
    doc.setTextColor('#1B2733');
    doc.text(wrapped, M + 168, y);
    y += wrapped.length * 13 + 4;
  };

  // ---------------- cover ----------------
  banner();
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(NAVY);
  doc.setFontSize(24);
  doc.text('Submission', M, y);
  doc.setFontSize(10);
  doc.setTextColor(ORANGE);
  doc.text('AGENTS ALLIANCE SERVICES', M, (y += 18));

  doc.setFont('helvetica', 'normal');
  doc.setFontSize(9);
  doc.setTextColor(MUTED);
  doc.text(new Date().toLocaleDateString('en-US', { dateStyle: 'long' }), W - M, 54, { align: 'right' });
  doc.text(input.id, W - M, 68, { align: 'right' });

  y += 26;
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(17);
  doc.setTextColor(NAVY);
  doc.text(String(data.businessName ?? 'Untitled'), M, y);

  const lines = (Array.isArray(data.lines) ? data.lines : [])
    .map((l) => LOB_NAME[l as keyof typeof LOB_NAME] ?? l)
    .join(' · ');
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(10);
  doc.setTextColor(MUTED);
  y += 16;
  doc.text([input.className, lines].filter(Boolean).join('   ·   '), M, y);
  y += 22;

  // ---------------- the account ----------------
  heading('The account');
  row('Physical address', String(data.physicalAddress ?? ''));
  row('Mailing address', String(data.mailingAddress ?? ''));
  row('Website', String(data.website ?? ''));
  row('FEIN', String(data.fein ?? ''));
  row('Years in business', String(data.yearsInBusiness ?? ''));
  row('SIC / NAICS', String(data.sicNaics ?? ''));
  row('Gross annual revenue', money(data.grossAnnualRevenue));
  row('Annual payroll', money(data.annualPayroll));
  row('Known losses', String(data.knownLosses ?? ''));
  row('Incumbent carrier', String(data.incumbentCarrier ?? ''));
  row('Quote needed by', String(data.quoteNeedBy ?? ''));
  row('Contact', [data.primaryContact, data.email, data.phone].filter(Boolean).join(' · '));
  y += 8;

  const ops = String(data.descriptionOfOps ?? '').trim();
  if (ops) {
    heading('Description of operations');
    const wrapped = doc.splitTextToSize(ops, W - M * 2) as string[];
    room(wrapped.length * 13);
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(10);
    doc.setTextColor('#1B2733');
    doc.text(wrapped, M, y);
    y += wrapped.length * 13 + 12;
  }

  const summary = String(data.executiveSummary ?? '').trim();
  if (summary) {
    heading('Executive summary');
    const wrapped = doc.splitTextToSize(summary, W - M * 2) as string[];
    room(wrapped.length * 13);
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(10);
    doc.setTextColor('#1B2733');
    doc.text(wrapped, M, y);
    y += wrapped.length * 13 + 12;
  }

  // ---------------- the questionnaire ----------------
  //
  // Rendered densely — label and answer on one line — because this is an
  // appendix inside a longer document, not the standalone form. A questionnaire
  // that spread over twelve pages here would bury the ACORDs behind it.
  if (input.supplemental) {
    const { form, answers } = input.supplemental;
    const visible = form.fields.filter((f) => !f.showIf || answers[f.showIf.field] === f.showIf.equals);
    heading(`${form.class} supplemental`);
    for (const section of form.sections) {
      const fields = visible.filter((f) => f.section === section.id);
      if (!fields.length) continue;
      room(30);
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(9.5);
      doc.setTextColor(NAVY);
      doc.text(section.title, M, y);
      y += 13;
      for (const f of fields) {
        const v = answers[f.id];
        const shown =
          typeof v === 'boolean' ? (v ? 'Yes' : 'No') : Array.isArray(v) ? `${v.length} row(s)` : String(v ?? '');
        const label = doc.splitTextToSize(f.label, W - M * 2 - 130) as string[];
        room(Math.max(label.length * 11, 12));
        doc.setFont('helvetica', 'normal');
        doc.setFontSize(8.5);
        doc.setTextColor(MUTED);
        doc.text(label, M + 8, y);
        doc.setFont('helvetica', shown ? 'bold' : 'normal');
        doc.setTextColor(shown ? '#1B2733' : '#B0B7BF');
        doc.text(shown || '—', W - M, y, { align: 'right' });
        y += Math.max(label.length * 11, 12);
      }
      y += 8;
    }
  }

  // ⚠️ coverOnly stops here. Drawing the cover is fast; MERGING is what locks
  // the page for tens of seconds, and when documents are attached individually
  // there is nothing to merge — just a summary page to lead with.
  if (input.coverOnly) {
    const only = doc.output('arraybuffer');
    return new Blob([only], { type: 'application/pdf' });
  }

  // ---------------- attachments: fetch, then list, then merge ----------------
  const rawDocs = (Array.isArray(data.documents) ? data.documents : []).filter((d) => d.storagePath);
  const fetched: { att: Attachment; bytes: ArrayBuffer | null }[] = [];
  for (const d of rawDocs) {
    const mime = String(d.mime ?? '');
    const att: Attachment = {
      kind: String(d.kind ?? 'other'),
      name: String(d.docName ?? d.label ?? 'Attachment'),
      storagePath: String(d.storagePath),
      mime,
      merged: false,
      pages: 0,
    };
    try {
      input.onProgress?.('Fetching documents', fetched.length + 1, rawDocs.length);
      await yieldToPaint();
      const url = await input.resolveDoc(att.storagePath);
      const res = await fetch(url);
      fetched.push({ att, bytes: res.ok ? await res.arrayBuffer() : null });
    } catch {
      fetched.push({ att, bytes: null });
    }
  }

  // Work out mergeability before printing the contents page, so the page tells
  // the truth about what follows it.
  const merger = await PDFDocument.create();
  for (const f of fetched) {
    if (!f.bytes) continue;
    try {
      if (f.att.mime === 'application/pdf') {
        const src = await PDFDocument.load(f.bytes, { ignoreEncryption: true });
        f.att.pages = src.getPageCount();
        f.att.merged = true;
      } else if (/^image\/(png|jpeg)$/.test(f.att.mime)) {
        f.att.pages = 1;
        f.att.merged = true;
      }
    } catch {
      // A corrupt or password-protected PDF stays listed as sent separately
      // rather than taking the whole package down with it.
      f.att.merged = false;
    }
  }

  heading('Attachments');
  if (!fetched.length) {
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(9.5);
    doc.setTextColor(MUTED);
    doc.text('None attached.', M, y);
    y += 16;
  }
  for (const { att } of fetched) {
    room(14);
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(9.5);
    doc.setTextColor('#1B2733');
    doc.text(att.name, M, y);
    doc.setTextColor(MUTED);
    doc.text(att.merged ? `${att.pages} page${att.pages === 1 ? '' : 's'}, included below` : 'sent separately', W - M, y, {
      align: 'right',
    });
    y += 14;
  }

  // Footer on every cover page.
  const coverPages = doc.getNumberOfPages();
  for (let p = 1; p <= coverPages; p++) {
    doc.setPage(p);
    doc.setDrawColor(RULE);
    doc.line(M, H - 44, W - M, H - 44);
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(7.5);
    doc.setTextColor(MUTED);
    doc.text(
      `${String(data.businessName ?? '')} · ${input.id} · prepared by Agents Alliance Services · ` +
        `completeness ${grade.grade} (${grade.score}/100)`,
      M,
      H - 31,
    );
  }

  // ---------------- merge ----------------
  const coverBytes = doc.output('arraybuffer');
  const cover = await PDFDocument.load(coverBytes);
  const copiedCover = await merger.copyPages(cover, cover.getPageIndices());
  copiedCover.forEach((p) => merger.addPage(p));

  let mergedCount = 0;
  const toMerge = fetched.filter((f) => f.bytes && f.att.merged).length;
  for (const f of fetched) {
    if (!f.bytes || !f.att.merged) continue;
    input.onProgress?.('Merging', ++mergedCount, toMerge);
    await yieldToPaint();
    try {
      if (f.att.mime === 'application/pdf') {
        const src = await PDFDocument.load(f.bytes, { ignoreEncryption: true });
        const pages = await merger.copyPages(src, src.getPageIndices());
        pages.forEach((p) => merger.addPage(p));
      } else {
        const img =
          f.att.mime === 'application/pdf'
            ? null
            : f.att.mime === 'image/png'
              ? await merger.embedPng(f.bytes)
              : await merger.embedJpg(f.bytes);
        if (img) {
          // Fit the image to a letter page rather than cropping it.
          const page = merger.addPage([612, 792]);
          const scale = Math.min((612 - 72) / img.width, (792 - 72) / img.height, 1);
          page.drawImage(img, {
            x: (612 - img.width * scale) / 2,
            y: (792 - img.height * scale) / 2,
            width: img.width * scale,
            height: img.height * scale,
          });
        }
      }
    } catch {
      // Already listed as "sent separately"; nothing further to do.
    }
  }

  // Copy into a fresh ArrayBuffer: pdf-lib returns a Uint8Array whose buffer is
  // typed as ArrayBufferLike, which Blob will not accept.
  const out = await merger.save();
  return new Blob([out.slice().buffer as ArrayBuffer], { type: 'application/pdf' });
}
